import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from '../../../store';
import { TableRow, InputAdornment, Box, IconButton, Button } from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    PersonOutline as PersonOutlineIcon,
    PersonAddAltOutlined as PersonAddAltOutlinedIcon,
    PersonOffOutlined as PersonOffOutlinedIcon,
    Link as LinkIcon
} from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AlertPopup from '../../../components/common/AlertPopup';
import { selectUserProfile } from '../../../modules/user';
import {
    PageHeader, PageTitle, AddWorkerButton, FilterBar, SearchInput,
    StatusTabsContainer, StyledTabs, StatusTab,
    StyledTableContainer, StyledTable, StyledTableBody, StyledTableCell,
    StatusTag, PaginationFooter,
    SummaryCardsContainer, SummaryCard, SummaryIconWrapper, SummaryContent, SummaryTitle, SummaryValue, SummaryDescription,
    WorkerListContainer
} from './styles';
import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import CustomPagination from '../../../components/CustomPagination';
import CustomTableHeader from '../../../components/CustomHeader';
import {
    fetchWorkerList,
    selectWorkerList,
    selectWorkerListError,
    selectWorkerListLoading,
    selectWorkerListCount,
    fetchWorkerCounts,
    selectWorkerCount
} from '../../../modules/worker';
import { IWorker } from '../../../modules/worker/types';
import Loader from '../../../components/common/Loader';
import PaginatedSearchSelect from '../../../components/PaginatedSearchSelect';
import { fetchFacilityList } from '../../../modules/facility';
import { IFacility } from '../../../modules/facility/types';
import { fetchQualificationList } from '../../../modules/qualification';
import { IQualification } from '../../../modules/qualification/types';
import { fetchLocationList } from '../../../modules/location';
import { ILocation } from '../../../modules/location/types';
import { FetchResponse } from '@/interfaces/paginatedSearchSelect.interface';
import { truncateLetters } from '../../../helpers';
import { IRole, fetchRoleList } from '../../../modules/role';

type RoleOptionType = IRole & { id: number };

type Order = 'asc' | 'desc';

interface Column {
    id: keyof IWorker | string;
    label: string;
    numeric?: boolean;
    disableSorting?: boolean;
}

type ShiftHistoryOptionType = IFacility & { id: number };

// Utility functions for client-side sorting (standard implementation)
function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

// Mapping of header titles to the corresponding worker property keys (required for sorting)
const COLUMN_MAPPING: { [key: string]: keyof IWorker | string } = {
    'ID No': 'worker_id',
    'Full Name': 'first_name',
    'Email ID': 'user.email',
    'Phone Number': 'user.phone_number',
    'Emergency Contact': 'emergency_contact',
    'Location Name': 'location_name',
    'Status': 'status',
    'Action Required': 'action_required',
};

// Define the columns structure needed by CustomTableHeader
const WORKER_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
    id: id,
    label: label,
    numeric: id === 'worker_id',
    disableSorting: !(id === 'first_name' || id === 'worker_id' || id === 'action_required'),
}));

// --- Component ---

const WorkersListPage: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const navigate: ReturnType<typeof useNavigate> = useNavigate();
    const workerList = useSelector(selectWorkerList);
    const loading = useSelector(selectWorkerListLoading);
    
    const userProfile = useSelector(selectUserProfile);
    const orgCode = userProfile?.data?.org_code;
    const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

    const handleCopyInviteLink = () => {
        if (orgCode) {
            const baseUrl = process.env.REACT_APP_WORKER_APP_URL || 'https://worker-talenthub.onedoorwayinternational.com';
            const inviteUrl = `${baseUrl}/?org_code=${orgCode}`;
            navigator.clipboard.writeText(inviteUrl);
            setAlert({ message: 'Worker invite link copied to clipboard!', severity: 'success' });
        }
    };
    const totalWorkers = useSelector(selectWorkerListCount);
    const workerCounts = useSelector(selectWorkerCount);
    const workerListError = useSelector(selectWorkerListError);

    const { setActivePage } = useSidebar();
    const { setPath, setIsShow } = useBreadcrumb();
    React.useEffect(() => {
        setActivePage('worker');
        setPath([
            { title: 'Management', path: '/' },
            { title: 'Worker', path: '/' },
        ]);
        setIsShow(false);
    }, [setActivePage, setPath, setIsShow]);

    const [tabValue, setTabValue] = useState(0);
    const [page, setPage] = useState(1);
    const rowsPerPage = 10; // Assuming 10 workers per page as per API

    const [selectedShiftHistory, setSelectedShiftHistory] = useState<ShiftHistoryOptionType | null>(null);
    const [selectedQualification, setSelectedQualification] = useState<IQualification | null>(null);
    const [selectedRole, setSelectedRole] = useState<RoleOptionType | null>(null);
    const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
    const [selectedExperience, setSelectedExperience] = useState<ExperienceOptionType | null>(null);
    const [yearsOfExperienceMin, setYearsOfExperienceMin] = useState<number | undefined>(undefined);
    const [yearsOfExperienceMax, setYearsOfExperienceMax] = useState<number | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [status, setStatus] = useState<string>('All');

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof IWorker | string>('action_required');

    const fetchShiftHistoryOptionsCallback = React.useCallback(async (search: string, page: number): Promise<FetchResponse<ShiftHistoryOptionType>> => {
        const response = await dispatch(fetchFacilityList({ page, pageSize: 10, search }));
        if (fetchFacilityList.fulfilled.match(response)) {
            const options = response.payload.results.map(facility => ({
                ...facility,
                id: facility.facility_id,
            }));
            return {
                options: options,
                hasMore: (page * 10) < response.payload.count,
            };
        } else {
            return {
                options: [],
                hasMore: false,
            };
        }
    }, [dispatch]);

    const fetchQualificationOptionsCallback = React.useCallback(async (search: string, page: number): Promise<FetchResponse<IQualification>> => {
        const response = await dispatch(fetchQualificationList({ page, pageSize: 10, search }));
        if (fetchQualificationList.fulfilled.match(response)) {
            const payload = response.payload as any;
            const rawOptions = payload.results || [];

            const processedOptions = rawOptions
                .filter((qual: any) => qual && qual.qualification_name)
                .map((qual: any) => ({
                    ...qual,
                    id: qual.id || qual.qualification_id || qual.qualification_name.replace(/\s+/g, '-'),
                }));

            const uniqueOptions = Array.from(new Map(processedOptions.map((item: any) => [item.id, item])).values()) as IQualification[];

            return {
                options: uniqueOptions,
                hasMore: (page * 10) < payload.count,
            };
        } else {
            return {
                options: [],
                hasMore: false,
            };
        }
    }, [dispatch]);

    const fetchRoleOptionsCallback = React.useCallback(async (search: string, page: number): Promise<FetchResponse<RoleOptionType>> => {
        const response = await dispatch(fetchRoleList({ page, pageSize: 10, search }) as any);
        if (response.payload && response.payload.results) {
            const roles: RoleOptionType[] = response.payload.results.map((r: IRole) => ({
                ...r,
                id: r.role_id,
            }));
            return { options: roles, hasMore: roles.length === 10 };
        }
        return { options: [], hasMore: false };
    }, [dispatch]);

    const fetchLocationOptionsCallback = React.useCallback(async (search: string, page: number): Promise<FetchResponse<ILocation>> => {
        const response = await dispatch(fetchLocationList({ page, pageSize: 10, search }));
        if (fetchLocationList.fulfilled.match(response)) {
            const payload = response.payload as any;
            const rawOptions = payload.results || [];

            const processedOptions = rawOptions
                .filter((loc: any) => loc && loc.location_name)
                .map((loc: any) => ({
                    ...loc,
                    id: loc.id || `${loc.location_name}-${loc.latitude}-${loc.longitude}`.replace(/\s+/g, '-'),
                }));

            const uniqueOptions = Array.from(new Map(processedOptions.map((item: any) => [item.id, item])).values()) as ILocation[];

            return {
                options: uniqueOptions,
                // Since the action hardcodes count to the length of the results array,
                // we assume if we got fewer than pageSize, there's no more.
                // But better yet, if the backend is not paginated, hasMore should be false.
                hasMore: uniqueOptions.length === 10,
            };
        } else {
            return {
                options: [],
                hasMore: false,
            };
        }
    }, [dispatch]);

    interface ExperienceOptionType {
        id: number;
        experience: string;
    }

    const experienceData: ExperienceOptionType[] = useMemo(() => [
        { id: 1, experience: "1 to 2" },
        { id: 2, experience: "2 to 3" },
        { id: 3, experience: "3 to 4" },
        { id: 4, experience: "4 to 5" },
        { id: 5, experience: "5 to above" },
    ], []);

    const fetchExperienceOptionsCallback = React.useCallback(async (search: string, page: number): Promise<FetchResponse<ExperienceOptionType>> => {
        const filteredOptions = experienceData.filter(option =>
            option.experience.toLowerCase().includes(search.toLowerCase())
        );
        const startIndex = (page - 1) * 10;
        const endIndex = startIndex + 10;
        const paginatedOptions = filteredOptions.slice(startIndex, endIndex);

        return {
            options: paginatedOptions,
            hasMore: endIndex < filteredOptions.length,
        };
    }, [experienceData]);

    useEffect(() => {
        dispatch(fetchWorkerCounts());
    }, [dispatch]);

    useEffect(() => {
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < 3) {
            return;
        }

        dispatch(fetchWorkerList({
            page,
            pageSize: rowsPerPage,
            qualificationId: selectedQualification?.qualification_id,
            locationName: selectedLocation?.location_name,
            yearsOfExperienceMin,
            yearsOfExperienceMax,
            facilityId: selectedShiftHistory?.facility_id || null,
            roleId: selectedRole?.role_id,
            searchTerm: trimmedSearchTerm,
            status,
            orderBy,
            order
        }));
    }, [
        dispatch,
        page,
        rowsPerPage,
        selectedQualification,
        selectedLocation,
        yearsOfExperienceMin,
        yearsOfExperienceMax,
        selectedShiftHistory,
        selectedRole,
        searchTerm,
        status,
        order,
        orderBy
    ]);

    const handleStatusChange = (newStatus: string) => {
        setStatus(newStatus);
        setPage(1);
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: string,
    ) => {
        const propertyKey = property as keyof IWorker | string;

        const columnDef = WORKER_COLUMNS.find(col => col.id === property);
        if (columnDef?.disableSorting) {
            return;
        }

        const isAsc = orderBy === propertyKey && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(propertyKey);
    };

    const sortedData = useMemo(() => {
        if (!workerList) return [];
        // Client-side sorting only for the current page's data
        return stableSort(workerList, getComparator(order, orderBy as keyof IWorker));
    }, [order, orderBy, workerList]);

    const totalPages = Math.ceil(totalWorkers / rowsPerPage);

    return (
        <Box>
            {loading && <Loader open={loading} />}
            {/* 1. Summary Cards Section */}
            <SummaryCardsContainer>
                <SummaryCard>
                    <SummaryIconWrapper colorName="primary">
                        <PersonOutlineIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>Total No of Workers</SummaryTitle>
                        <SummaryValue>{workerCounts.all_workers}</SummaryValue>
                        <SummaryDescription>{workerCounts.pending_workers} onboarding</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
                <SummaryCard>
                    <SummaryIconWrapper colorName="info">
                        <PersonAddAltOutlinedIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>New Registration</SummaryTitle>
                        <SummaryValue>{workerCounts.new_registrations}</SummaryValue>
                        <SummaryDescription>Last 30 Days</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
                <SummaryCard>
                    <SummaryIconWrapper colorName="error">
                        <PersonOffOutlinedIcon />
                    </SummaryIconWrapper>
                    <SummaryContent>
                        <SummaryTitle>Suspended Workers</SummaryTitle>
                        <SummaryValue>{workerCounts.suspended_workers}</SummaryValue>
                        <SummaryDescription>Require Action</SummaryDescription>
                    </SummaryContent>
                </SummaryCard>
            </SummaryCardsContainer>

            <WorkerListContainer>
                {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
                {/* 2. Page Header and Add Worker Button */}
                <PageHeader>
                    <PageTitle>Workers List</PageTitle>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {orgCode && (
                            <Button 
                                variant="outlined" 
                                startIcon={<LinkIcon />} 
                                onClick={handleCopyInviteLink}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    px: 2,
                                    borderColor: '#5E8E7E',
                                    color: '#5E8E7E',
                                    '&:hover': {
                                        borderColor: '#5E8E7E',
                                        backgroundColor: 'rgba(94, 142, 126, 0.08)'
                                    }
                                }}
                            >
                                Copy Invite Link
                            </Button>
                        )}
                        <AddWorkerButton href='/worker/add' startIcon={<AddCircleOutlineIcon />}>
                            Add New Worker
                        </AddWorkerButton>
                    </Box>
                </PageHeader>

                {/* 3. Filter Bar (unchanged) */}
                <FilterBar>
                    <SearchInput
                        placeholder="Search by Name, ID..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value as string)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchTerm && (
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={() => setSearchTerm('')}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <PaginatedSearchSelect
                        fetchOptions={fetchShiftHistoryOptionsCallback}
                        getOptionLabel="facility_name"
                        label="Shift History"
                        value={selectedShiftHistory}
                        onChange={(newValue) => setSelectedShiftHistory(newValue as ShiftHistoryOptionType | null)}
                        placeholder="Select Shift History"
                    />
                    <PaginatedSearchSelect
                        fetchOptions={fetchExperienceOptionsCallback}
                        getOptionLabel="experience"
                        label="Experience"
                        value={selectedExperience}
                        onChange={(newValue: any) => {
                            if (Array.isArray(newValue)) return;
                            const val = newValue as ExperienceOptionType | null;
                            setSelectedExperience(val);
                            if (val) {
                                const [min, max] = val.experience.split(' to ').map((s: string) => parseInt(s));
                                setYearsOfExperienceMin(min);
                                setYearsOfExperienceMax(val.experience === "5 to above" ? undefined : max);
                            } else {
                                setYearsOfExperienceMin(undefined);
                                setYearsOfExperienceMax(undefined);
                            }
                        }}
                        placeholder="Select Experience"
                    />
                    <PaginatedSearchSelect
                        fetchOptions={fetchQualificationOptionsCallback}
                        getOptionLabel="qualification_name"
                        label="Qualification"
                        value={selectedQualification}
                        onChange={(newValue) => setSelectedQualification(newValue as IQualification | null)}
                        placeholder="Select Qualification"
                    />
                    <PaginatedSearchSelect
                        fetchOptions={fetchRoleOptionsCallback}
                        getOptionLabel="role_name"
                        label="Role"
                        value={selectedRole}
                        onChange={(newValue) => setSelectedRole(newValue as RoleOptionType | null)}
                        placeholder="Select Role"
                    />
                    <PaginatedSearchSelect
                        fetchOptions={fetchLocationOptionsCallback}
                        getOptionLabel="location_name"
                        label="Location"
                        value={selectedLocation}
                        onChange={(newValue) => setSelectedLocation(newValue as ILocation | null)}
                        placeholder="Select Location"
                    />
                </FilterBar>

                {/* 4. Status Tabs (unchanged) */}
                <StatusTabsContainer>
                    <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="status tabs">
                        <StatusTab
                            label={"All (" + workerCounts.all_workers + ")"}
                            onClick={() => handleStatusChange('')}
                        />
                        <StatusTab
                            label={"Pending (" + workerCounts.pending_workers + ")"}
                            onClick={() => handleStatusChange('pending')}
                        />
                        <StatusTab
                            label={"Suspended (" + workerCounts.suspended_workers + ")"}
                            onClick={() => handleStatusChange('suspended')}
                        />
                        <StatusTab
                            label={"Approved (" + workerCounts.approved_workers + ")"}
                            onClick={() => handleStatusChange('active')}
                        />
                    </StyledTabs>
                </StatusTabsContainer>

                <StyledTableContainer>
                    <StyledTable stickyHeader>
                        <CustomTableHeader
                            columns={WORKER_COLUMNS}
                            order={order}
                            orderBy={orderBy as string}
                            onRequestSort={handleRequestSort}
                        />

                        <StyledTableBody>
                            {workerListError && sortedData.length <= 0 && (
                                <TableRow

                                    hover
                                    sx={{ cursor: "pointer" }}
                                >
                                    <StyledTableCell colSpan={8} align="center">
                                        No data available
                                    </StyledTableCell>
                                </TableRow>
                            )}

                            {!workerListError && sortedData && sortedData.length > 0 && sortedData.map((worker) => (
                                <TableRow
                                    key={worker.worker_id}
                                    hover
                                    onClick={() => { navigate(`/worker/${worker.worker_id}/`) }}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <StyledTableCell>{worker.worker_id}</StyledTableCell>
                                    <StyledTableCell>{`${worker.user.first_name} ${worker.user.last_name}`}</StyledTableCell>
                                    <StyledTableCell>{worker.user.email}</StyledTableCell>
                                    <StyledTableCell>{worker.user.phone_number}</StyledTableCell>
                                    <StyledTableCell>{worker.emergency_contact}</StyledTableCell>
                                    <StyledTableCell>{truncateLetters(worker.location_name, 20)}</StyledTableCell>
                                    <StyledTableCell>
                                        <StatusTag status={worker.status}>
                                            {worker.status.toUpperCase()}
                                        </StatusTag>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <StatusTag status={worker.action_required ? 'pending' : 'approved'}>
                                            {worker.action_required ? 'Take Action' : 'No Action Required'}
                                        </StatusTag>
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                        </StyledTableBody>
                    </StyledTable>
                </StyledTableContainer>
            </WorkerListContainer>

            {/* 6. Pagination Footer */}
            <PaginationFooter>
                <Box>
                    Showing {workerList?.length || 0} of {totalWorkers} results
                </Box>
                <CustomPagination
                    totalPages={totalPages}
                    currentPage={page}
                    onPageChange={handlePageChange}
                />
            </PaginationFooter>
        </Box>
    );
};

export default WorkersListPage;